import type { AICheckerTrace, FlaggedProductWithRestaurant } from '../../../../../../services/admin/adminService';
import { isActiveRequestStatus, qtyFlagRestaurants } from '../../../../../customerRequests/quantityFlagRestaurant';
import { aiQtyFlagKey } from './aiQtyFlagKey';

// The wording comes from server/services/quantityUnknownPack.ts. Stored reasons carry no machine-readable
// kind, so the text is the only handle on rows that already exist.
const ASK_CUSTOMER_REASON = /ask the customer/i;

export function isAskCustomerFlag(flag: FlaggedProductWithRestaurant): boolean {
  return ASK_CUSTOMER_REASON.test(flag.reason ?? '');
}

// Flags neither marked done nor filed as known. Older rows may hold only the count, not the array.
export function openQuantityFlagCount(trace: Pick<AICheckerTrace, 'quantityFlags' | 'quantityFlagCount'>): number {
  const flags = trace.quantityFlags ?? [];
  return flags.length ? flags.filter((f) => !f.resolved && !f.known).length : trace.quantityFlagCount;
}

// True when open quantity flags are the ONLY thing wrong with this email. Anything else the row can show
// as a problem keeps it out: a wrong/inconclusive verdict, email issues, a missing factuur, a manual mark,
// or not being an order email at all.
export function isQtyFlagOnlyIssue(trace: AICheckerTrace): boolean {
  if (openQuantityFlagCount(trace) === 0) return false;
  if (trace.matchedIn !== 'order' || trace.manualIssue) return false;
  if (trace.emailVerdict === 'wrong' || trace.emailVerdict === 'inconclusive') return false;
  if ((trace.emailIssues ?? []).length > 0) return false;
  if (trace.factuurExpected && !trace.factuurCreated) return false;
  return true;
}

// Same product name under the same supplier = one issue. The supplier stays in the key because the
// table groups per supplier, and two suppliers sharing a name are two different products.
// An ask-customer flag is also per restaurant: each restaurant gets its own question, so each needs its own row.
export function qtyFlagProductKey(flag: FlaggedProductWithRestaurant): string {
  const productKey = `${flag.platform}|${(flag.productName ?? '').trim().replace(/\s+/g, ' ').toLowerCase()}`;
  if (!isAskCustomerFlag(flag)) return productKey;
  const restaurantIds = Array.from(new Set(qtyFlagRestaurants(flag).map((r) => r.restaurantId))).sort();
  return restaurantIds.length > 0 ? `${productKey}|${restaurantIds.join(',')}` : productKey;
}

export function groupFlagsByProduct(flags: FlaggedProductWithRestaurant[]): Map<string, FlaggedProductWithRestaurant[]> {
  const groups = new Map<string, FlaggedProductWithRestaurant[]>();
  for (const flag of flags) {
    const key = qtyFlagProductKey(flag);
    const group = groups.get(key);
    if (group) group.push(flag);
    else groups.set(key, [flag]);
  }
  return groups;
}

// React key per row. `aiQtyFlagKey` is one flag's identity for the done/known endpoints, but a multi-delivery
// factuur can list one product on several lines, so it repeats within a table. Rows with a repeated React key
// survive in the DOM after a filter removes them, showing flags that no longer belong to the list.
export function qtyFlagRowKeys(flags: FlaggedProductWithRestaurant[]): Map<FlaggedProductWithRestaurant, string> {
  const seen = new Map<string, number>();
  const rowKeys = new Map<FlaggedProductWithRestaurant, string>();
  for (const flag of flags) {
    const key = aiQtyFlagKey(flag);
    const occurrence = seen.get(key) ?? 0;
    seen.set(key, occurrence + 1);
    rowKeys.set(flag, `${key}#${occurrence}`);
  }
  return rowKeys;
}

export type QtyFlagAskState = 'toAsk' | 'pending' | 'approved';

// Rejected, cancelled and never-asked restaurants all still need a question. Unresolved flags are always
// toAsk: without a restaurant there is nothing that can prove the question was asked everywhere.
export function qtyFlagAskState(flag: FlaggedProductWithRestaurant): QtyFlagAskState {
  const restaurants = qtyFlagRestaurants(flag);
  if (restaurants.length === 0) return 'toAsk';
  const statusByRestaurant = new Map((flag.customerRequests ?? []).map((r) => [r.restaurantId, r.status]));
  const statuses = restaurants.map((r) => statusByRestaurant.get(r.restaurantId));
  if (statuses.some((s) => !s || (!isActiveRequestStatus(s) && s !== 'completed'))) return 'toAsk';
  return statuses.some((s) => s && isActiveRequestStatus(s)) ? 'pending' : 'approved';
}

// Whole days since the question was created, for the row chip. Null when the date can't be read.
export function requestAgeDays(createdAt: string, now = Date.now()): number | null {
  const then = new Date(createdAt).getTime();
  return Number.isFinite(then) ? Math.max(0, Math.floor((now - then) / 86_400_000)) : null;
}

// Same rule as the per-supplier recheck: inconclusive means nothing was audited, unchanged means the data was
// never fixed. Neither may close a flag.
export function isCleanRecheck(result: { stillFlagged: boolean; inconclusive?: boolean; unchanged?: boolean }): boolean {
  return !result.stillFlagged && !result.inconclusive && !result.unchanged;
}

export type QtyRecheckOutcome = { outcome: 'closed' | 'open' | 'failed'; fileVerified?: boolean; error?: string };
export type QtyRecheckSummary = { total: number; closed: number; failed: number; filesVerified: number; tone: 'success' | 'partial' | 'error'; error?: string };

// One pill for a row that rechecked several flags.
export function summarizeQtyRecheck(outcomes: QtyRecheckOutcome[]): QtyRecheckSummary {
  const closed = outcomes.filter((o) => o.outcome === 'closed');
  const failed = outcomes.filter((o) => o.outcome === 'failed');
  const tone = closed.length === outcomes.length ? 'success' : failed.length > 0 && closed.length === 0 ? 'error' : 'partial';
  return { total: outcomes.length, closed: closed.length, failed: failed.length, filesVerified: closed.filter((o) => o.fileVerified).length, tone, error: failed[0]?.error };
}
